import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ["slide", "indicator"]
  static get values() {
    return {
      currentIndex: Number
    };
  }

  connect() {
    console.log('Shortlist Notification Controller Connected');
    this.totalSlides = this.slideTargets.length;
    console.log(`Found ${this.totalSlides} slides`);
    this.showSlide(this.currentIndexValue);
    
    this.addBodySpacing();
    
    this.markInfoBannerAsViewed();
  }

  disconnect() {
    this.removeBodySpacing();
  }

  markInfoBannerAsViewed() {
    const infoBanner = this.slideTargets.find(slide => 
      slide.dataset.notificationType === 'info'
    );
    
    if (infoBanner) {
      console.log('Info banner detected, marking as viewed automatically');
      fetch('/users/mark_info_banner_viewed', {
        method: 'POST',
        headers: this._headers()
      }).then(response => {
        if (response.ok) {
          console.log('Info banner marked as viewed successfully');
        } else {
          console.error('Failed to mark info banner as viewed:', response.status);
        }
      }).catch(error => {
        console.error('Error marking info banner as viewed:', error);
      });
    }
  }

  addBodySpacing() {
    const mainContent = document.querySelector('.flex.flex-1');
    if (mainContent) {
      const banner = document.getElementById('shortlist-notification-banner');
      if (banner) {
        const bannerHeight = banner.offsetHeight;
        mainContent.style.marginTop = `${bannerHeight}px`;
      }
    }
  }

  removeBodySpacing() {
    const mainContent = document.querySelector('.flex.flex-1');
    if (mainContent) {
      mainContent.style.marginTop = '';
    }
  }

  nextSlide() {
    console.log('Next slide triggered');
    this.currentIndexValue = (this.currentIndexValue + 1) % this.totalSlides;
    this.showSlide(this.currentIndexValue);
  }

  prevSlide() {
    console.log('Previous slide triggered');
    this.currentIndexValue = (this.currentIndexValue - 1 + this.totalSlides) % this.totalSlides;
    this.showSlide(this.currentIndexValue);
  }

  goToSlide(event) {
    const index = parseInt(event.params.slideIndex);
    console.log(`Go to slide: ${index}`);
    if (!isNaN(index)) {
      this.currentIndexValue = index;
      this.showSlide(index);
    }
  }

  showSlide(index) {
    console.log(`Showing slide ${index}`);
    
    this.slideTargets.forEach(slide => {
      slide.classList.remove('active');
    });

    if (this.slideTargets[index]) {
      this.slideTargets[index].classList.add('active');
    }

    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((indicator, i) => {
        if (i === index) {
          indicator.classList.add('active');
        } else {
          indicator.classList.remove('active');
        }
      });
    }
  }

  closeCurrent(event) {
    event.preventDefault();
    
    console.log('Closing current notification');
    
    const currentSlide = this.slideTargets[this.currentIndexValue];
    const notificationType = currentSlide.dataset.notificationType;
    
    if (notificationType === 'info') {
      console.log('Closing info banner (already marked as viewed)');
      
      currentSlide.remove();
      
      if (this.hasIndicatorTarget && this.indicatorTargets[this.currentIndexValue]) {
        this.indicatorTargets[this.currentIndexValue].remove();
      }
      
      this.totalSlides = this.slideTargets.length;
      console.log(`Remaining slides: ${this.totalSlides}`);
      
      if (this.totalSlides === 0) {
        console.log('No more slides, hiding banner');
        this.element.classList.add('hidden');
        this.removeBodySpacing();
        return;
      }
      
      if (this.currentIndexValue >= this.totalSlides) {
        this.currentIndexValue = this.totalSlides - 1;
      }
      
      this.showSlide(this.currentIndexValue);
      this.updateCarouselControls();
      return;
    }
    
    const profileId = currentSlide.dataset.profileId;
    const projectId = currentSlide.dataset.projectId;
    const submissionBoardId = currentSlide.dataset.submissionBoardId;
    
    if (profileId && notificationType && projectId && submissionBoardId) {
      fetch(`/api/v1/projects/${projectId}/submission_boards/${submissionBoardId}/sb_profiles/${profileId}/mark_notification_viewed`, {
        method: 'PATCH',
        headers: this._headers(),
        body: JSON.stringify({ notification_type: notificationType })
      }).then(response => {
        console.log('Notification marked as viewed:', response.status);
      }).catch(error => {
        console.error('Error marking notification as viewed:', error);
      });
    }
    
    currentSlide.remove();
    
    if (this.hasIndicatorTarget && this.indicatorTargets[this.currentIndexValue]) {
      this.indicatorTargets[this.currentIndexValue].remove();
    }
    
    this.totalSlides = this.slideTargets.length;
    console.log(`Remaining slides: ${this.totalSlides}`);
    
    if (this.totalSlides === 0) {
      console.log('No more slides, hiding banner');
      this.element.classList.add('hidden');
      this.removeBodySpacing();
      return;
    }
    
    if (this.currentIndexValue >= this.totalSlides) {
      this.currentIndexValue = this.totalSlides - 1;
    }
    
    this.showSlide(this.currentIndexValue);
    
    this.updateCarouselControls();
  }

  updateCarouselControls() {
    const arrows = this.element.querySelectorAll('.carousel-arrow');
    const indicators = this.element.querySelector('.carousel-indicators');
    
    if (this.totalSlides <= 1) {
      arrows.forEach(arrow => arrow.style.display = 'none');
      if (indicators) indicators.style.display = 'none';
    }
  }

  markAsViewed(event) {
    event.preventDefault();
    
    const element = event.currentTarget;
    const profileId = element.dataset.shortlistNotificationProfileIdParam;
    const notificationType = element.dataset.shortlistNotificationNotificationTypeParam;
    const targetUrl = element.href;
    
    const slideElement = element.closest('[data-shortlist-notification-target="slide"]');
    const projectId = slideElement?.dataset.projectId;
    const submissionBoardId = slideElement?.dataset.submissionBoardId;
    
    console.log(`Marking as viewed: ${profileId}, ${notificationType}`);
    console.log(`Project ID: ${projectId}, Submission Board ID: ${submissionBoardId}`);
    console.log(`Target URL: ${targetUrl}`);
    
    if (!profileId || !notificationType || !projectId || !submissionBoardId) {
      console.log('Missing required data, redirecting anyway');
      window.location.href = targetUrl;
      return;
    }

    const redirectUrl = targetUrl;
    
    fetch(`/api/v1/projects/${projectId}/submission_boards/${submissionBoardId}/sb_profiles/${profileId}/mark_notification_viewed`, {
      method: 'PATCH',
      headers: this._headers(),
      body: JSON.stringify({ notification_type: notificationType }),
      redirect: 'manual'
    }).then(response => {
      console.log('Response status:', response.status);
      console.log('About to redirect to:', redirectUrl);
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 100);
    }).catch(error => {
      console.error('Error marking as viewed:', error);
      console.log('Error occurred, still redirecting to:', redirectUrl);
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 100);
    });
  }

  closeAll(event) {
    event.preventDefault();
    
    console.log('Closing all notifications');
    
    const requests = this.slideTargets
      .map(slide => {
        const notificationType = slide.dataset.notificationType;
        
        if (notificationType === 'info') return null;
        
        const profileId = slide.dataset.profileId;
        const projectId = slide.dataset.projectId;
        const submissionBoardId = slide.dataset.submissionBoardId;
        
        if (!profileId || !notificationType || !projectId || !submissionBoardId) return null;
        
        return fetch(`/api/v1/projects/${projectId}/submission_boards/${submissionBoardId}/sb_profiles/${profileId}/mark_notification_viewed`, {
          method: 'PATCH',
          headers: this._headers(),
          body: JSON.stringify({ notification_type: notificationType })
        });
      })
      .filter(Boolean);

    Promise.all(requests).then(() => {
      this.element.remove();
      this.removeBodySpacing();
    }).catch(error => {
      console.error('Error closing notifications:', error);
      this.element.remove();
      this.removeBodySpacing();
    });
  }

  _headers() {
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
    };
  }
}